import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import VisaCard from "assets/svg/visaCard";
import Mastercard from "assets/svg/mastercard";
import Amex from "assets/svg/amex";
import Diners from "assets/svg/diners";
import Discover from "assets/svg/discover";
import JCB from "assets/svg/jcb";
import UnionPay from "assets/svg/unionpay";

import Button from "designV2/components/Button";
import { endPoint } from "utils/config";
import logo from "assets/img/sparkLoginLogo.svg";
import OrderPaymentForm from "./OrderPaymentForm";
import "layouts/shopAdmin/standalonePage.css";
import "./orderPayment.css";

/**
 * What each kind of payment talks to.
 *
 * The three cases used to be three copies of the same eighty lines,
 * which is how the trip branch ended up showing a blank error banner
 * and the order branch ended up with a different retry path.
 *
 * `tripPay` deliberately sends the payment method where the other
 * flows send the intent id. That is how the backend has always been
 * called from here and it is not obviously right, but changing it is a
 * backend question, not a redesign one.
 */
const FLOWS = {
  order: {
    title: "Pay for your order",
    intentUrl: (props) => `${window.env.API_URL}/api/v1/payment/order/${props.orderId}`,
    methodsUrl: (props) =>
      `${window.env.API_URL}/api/v1/payment/order/${props.orderId}/get-payment-methods`,
    confirm: "confirmCardPayment",
    webhookUrl: () => `${window.env.API_URL}/api/v1/alter/webhook/paymentIntent/Success`,
    webhookBody: (props, result) => ({
      metadata: { orderId: props.orderId },
      paymentIntent: result.paymentIntent.id,
    }),
  },
  trip: {
    title: "Add a card for your trip",
    intentUrl: (props) => `${window.env.API_URL}/api/v1/payment/trip/${props.tripId}`,
    methodsUrl: (props) =>
      `${window.env.API_URL}/api/v1/payment/trip/${props.tripId}/get-payment-methods`,
    confirm: "confirmCardSetup",
    webhookUrl: () => `${window.env.API_URL}/api/v1/alter/webhook/payment/setupIntentSuccess`,
    webhookBody: (props, result) => ({
      tripId: props.tripId,
      paymentMethod: result.setupIntent.payment_method,
      setupIntentId: result.setupIntent.id,
    }),
  },
  tripPay: {
    title: "Pay for your trip",
    intentUrl: (props) => `${window.env.API_URL}/api/v1/pay/trip/${props.tripId}`,
    methodsUrl: (props) =>
      `${window.env.API_URL}/api/v1/payment/trip/${props.tripId}/get-payment-methods`,
    confirm: "confirmCardPayment",
    webhookUrl: () => `${window.env.API_URL}/api/v1/alter/webhook/paymentIntent/Success`,
    webhookBody: (props, result) => ({
      metadata: { tripId: props.tripId },
      paymentIntent: result.paymentIntent.payment_method,
    }),
  },
};

const BRAND_ICONS = {
  visa: VisaCard,
  mastercard: Mastercard,
  amex: Amex,
  diners: Diners,
  discover: Discover,
  jcb: JCB,
  unionpay: UnionPay,
};

const brandIcon = (brand) => {
  const Icon = BRAND_ICONS[String(brand || "").toLowerCase()];
  return Icon ? <Icon width={28} height={28} /> : <CreditCardIcon />;
};

const brandName = (brand) => {
  const value = String(brand || "card");
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * The sentence worth showing when a request fails.
 *
 * The old code read `err.error.raw.message` after checking `err.raw`,
 * which threw inside its own error handler whenever the shapes did not
 * line up — so a failed payment could take the page down with it.
 */
const describeError = (error) => {
  const data = error && error.response && error.response.data;
  if (data) {
    if (typeof data.error === "string") return data.error;
    if (data.error && data.error.raw && data.error.raw.message) return data.error.raw.message;
    if (data.error && data.error.message) return data.error.message;
    if (typeof data.message === "string") return data.message;
  }
  if (error && error.message) return error.message;
  return "Something went wrong. Please try again.";
};

const bodyError = (body) => {
  if (!body || !body.error) return null;
  if (typeof body.error === "string") return body.error;
  if (body.error.raw && body.error.raw.message) return body.error.raw.message;
  return body.error.message || "The payment could not be started.";
};

/** Stripe says a saved card belongs to a different customer. */
const isWrongCustomer = (message) =>
  typeof message === "string" &&
  message.indexOf("belongs to the Customer") !== -1 &&
  message.indexOf("Please include the Customer") !== -1;

const NEW_CARD = "new";

/**
 * OrderPaymentFormContainer - the card form the apps open in a browser.
 *
 * Saved cards are chosen from the list on the page rather than from a
 * dialog behind a button, which is one tap instead of three on the
 * phone this is nearly always opened on.
 */
const OrderPaymentFormContainer = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const flow = FLOWS[props.for];

  const [savedMethods, setSavedMethods] = useState([]);
  const [selected, setSelected] = useState(NEW_CARD);
  const [cardComplete, setCardComplete] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!flow) return;

    axios
      .get(flow.methodsUrl(props), {
        headers: {
          "Access-Control-Allow-Origin": "*",
          authorization: props.token,
        },
      })
      .then((res) => {
        const methods = (res.data && res.data.paymentMethods) || [];

        // Stripe hands back one entry per intent, so the same physical
        // card shows up repeatedly. Keep the first of each.
        const unique = methods.filter(
          (method, index, all) =>
            index ===
            all.findIndex(
              (other) =>
                other.card.last4 === method.card.last4 &&
                other.card.exp_month === method.card.exp_month &&
                other.card.exp_year === method.card.exp_year
            )
        );

        setSavedMethods(unique);
        // A saved card that has no customer attached cannot be charged,
        // so it is not worth offering as the default.
        const usable = unique.filter((method) => method.customer);
        if (usable.length) setSelected(usable[0]);
      })
      // Nothing was fetched, so the new-card form is the whole page.
      // The previous version left this rejection unhandled.
      .catch(() => setSavedMethods([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leave = (outcome) => {
    window.location = `${endPoint}/payment/${outcome}?for=${props.for}`;
  };

  const fallBackToNewCard = (message) => {
    setError(message);
    setSelected(NEW_CARD);
    setCardComplete(false);
    setPaying(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (paying || !stripe || !elements || !flow) return;

    const savedMethod = selected === NEW_CARD ? null : selected;
    setPaying(true);
    setError("");

    axios
      .post(
        flow.intentUrl(props),
        savedMethod && savedMethod.customer ? { customerId: savedMethod.customer } : {},
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            authorization: props.token,
          },
        }
      )
      .then((res) => {
        const failed = bodyError(res.data);
        if (failed) {
          setPaying(false);
          setError(failed);
          return null;
        }

        return stripe[flow.confirm](res.data.client_secret, {
          payment_method: savedMethod
            ? savedMethod.id
            : { card: elements.getElement(CardElement) },
        });
      })
      .then((result) => {
        if (!result) return null;

        // Stripe reports a decline by resolving with an `error`, not by
        // rejecting — so the old `.catch` never ran for one, and the
        // success path then read a payment intent that was not there.
        if (result.error) {
          const message = result.error.message;
          if (isWrongCustomer(message)) {
            fallBackToNewCard("That saved card cannot be used here. Please enter a card.");
            return null;
          }
          setPaying(false);
          setError(message || "Your card was declined.");
          return null;
        }

        return axios.post(flow.webhookUrl(), flow.webhookBody(props, result), {
          headers: {
            "Access-Control-Allow-Origin": "*",
            authorization: props.token,
          },
        });
      })
      .then((confirmed) => {
        if (confirmed) leave("success");
      })
      .catch((err) => {
        const message = describeError(err);
        if (isWrongCustomer(message) || message.toLowerCase().indexOf("customer") !== -1) {
          fallBackToNewCard("That saved card cannot be used here. Please enter a card.");
          return;
        }
        setPaying(false);
        setError(message);
      });
  };

  if (!flow) {
    return (
      <div className="standalonePage">
        <div className="standaloneInner isNarrow">
          <div className="standaloneCard">
            <div className="standaloneResult">
              <span className="standaloneResultIcon danger">
                <ErrorOutlineIcon />
              </span>
              <h1 className="standaloneResultTitle">This link is not valid</h1>
              <p className="standaloneResultText">
                Open the payment page from the Spark app again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canPay = selected !== NEW_CARD || cardComplete;

  return (
    <div className="standalonePage">
      <div className="standaloneInner isNarrow">
        <div className="standaloneBrand">
          <img src={logo} alt="Spark" />
        </div>

        <div className="standaloneHead">
          <h1 className="standaloneTitle">{flow.title}</h1>
          <p className="standaloneSubtitle">
            Your card details go straight to Stripe. Spark never sees them.
          </p>
        </div>

        <div className="standaloneCard">
          <form className="standaloneForm" onSubmit={handleSubmit}>
            {error && (
              <div className="dsFormMessage error" role="alert">
                <ErrorOutlineIcon />
                <span>{error}</span>
              </div>
            )}

            {savedMethods.length > 0 && (
              <div className="paymentSection">
                <span className="paymentSectionTitle">Your cards</span>
                <div className="paymentMethodList">
                  {savedMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      className={`paymentMethod ${selected === method ? "isSelected" : ""}`}
                      onClick={() => setSelected(method)}
                    >
                      <span className="paymentMethodIcon">{brandIcon(method.card.brand)}</span>
                      <span className="paymentMethodBody">
                        <strong>
                          {brandName(method.card.brand)}{" "}
                          <span className="paymentMethodNumber">
                            &bull;&bull;&bull;&bull; {method.card.last4}
                          </span>
                        </strong>
                        <span>
                          Expires {method.card.exp_month}/{method.card.exp_year}
                        </span>
                      </span>
                      <span className="paymentMethodCheck">
                        <CheckCircleIcon />
                      </span>
                    </button>
                  ))}

                  <button
                    type="button"
                    className={`paymentMethod ${selected === NEW_CARD ? "isSelected" : ""}`}
                    onClick={() => setSelected(NEW_CARD)}
                  >
                    <span className="paymentMethodIcon">
                      <CreditCardIcon />
                    </span>
                    <span className="paymentMethodBody">
                      <strong>Use another card</strong>
                    </span>
                    <span className="paymentMethodCheck">
                      <CheckCircleIcon />
                    </span>
                  </button>
                </div>
              </div>
            )}

            {selected === NEW_CARD && (
              <div className="paymentSection">
                {savedMethods.length > 0 && (
                  <span className="paymentSectionTitle">Card details</span>
                )}
                <OrderPaymentForm
                  onChange={(event) =>
                    setCardComplete(Boolean(event.complete) && !event.error && !event.empty)
                  }
                />
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={paying}
              disabled={!stripe || !canPay}
            >
              {paying ? "Processing" : flow.confirm === "confirmCardSetup" ? "Save card" : "Pay now"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderPaymentFormContainer;
